import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';

// Common Configurations
const buttonTransition = '0.3s';
const borderSize = '2px';

const buttonFocus = css`
  outline: solid ${({ theme }) => theme.c.border.focus} 2px !important;
`;

const tertiaryButtonStyles = {
  general: css`
    border-color: transparent;
    background-color: transparent;
    border-radius: 2px;

    & > .icon {
      transition-property: transform;
      transition-duration: ${buttonTransition};
    }
  `,
  hoverIconMovement: css`
    & > .icon--right {
      transform: translateX(+4px)
    }

    & > .icon--left {
      transform: translateX(-4px)
    }
  `,
  pressedIconMovement: css`
    & > .icon--right {
      transform: translateX(-4px)
    }

    & > .icon--left {
      transform: translateX(+4px)
    }
  `,
};

// Sizes
const customThemingPaddings = css`
  // Need to override tertiary button paddings

  ${({ buttonTheming }) => (
    buttonTheming.includes('tertiary')
      ? 'padding: 0 4px;'
      : ''
  )}
`;

const iconOnlyButtonSizes = {
  small: css`
    padding: 8px;
  `,
  medium: css`
    padding: 10px;
  `,
  large: css`
    padding: 10px;

    & > .icon {
      width: 32px;
      height: 32px;
    }
  `,
};

const buttonSizes = {
  small: css`
    ${({ theme }) => theme.typographyRebrand.labels.buttonsSm}
    padding: 8px 24px;

    ${({ hasOnlyIcon }) => hasOnlyIcon && iconOnlyButtonSizes.small}
    ${customThemingPaddings}
  `,
  medium: css`
    ${({ theme }) => theme.typographyRebrand.labels.buttonsMd}
    padding: 10px 46px;

    ${({ hasOnlyIcon }) => hasOnlyIcon && iconOnlyButtonSizes.medium}
    ${customThemingPaddings}
  `,
  large: css`
    ${({ theme }) => theme.typographyRebrand.labels.buttonsLg}
    padding: 14px 56px;

    ${({ hasOnlyIcon }) => hasOnlyIcon && iconOnlyButtonSizes.large}
    ${customThemingPaddings}
  `,
};

const buttonDynamicSizes = {
  small: buttonSizes.small,
  medium: css`
    ${buttonSizes.small}

    @media screen and (min-width: ${(p) => p.theme.minWidthBreakpoint.tablet}px) {
      ${buttonSizes.medium}
    }
  `,
  large: css`
    ${buttonSizes.medium}

    @media screen and (min-width: ${(p) => p.theme.minWidthBreakpoint.tablet}px) {
      ${buttonSizes.large}
    }
  `,
};

// Variants
const buttonThemes = {
  'primary-light': css`
    background-color: ${({ theme }) => theme.c.button.light.primary.background.default};
    color: ${({ theme }) => theme.c.button.light.primary.text.default};
    border: solid ${({ theme }) => theme.c.button.light.primary.border.default} ${borderSize};

    & > .icon {
      color: ${({ theme }) => theme.c.button.light.primary.icon.default};
    }

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.c.button.light.primary.background.hover};
      color: ${({ theme }) => theme.c.button.light.primary.text.hover};
      border-color: ${({ theme }) => theme.c.button.light.primary.border.hover};

      & > .icon {
        color: ${({ theme }) => theme.c.button.light.primary.icon.hover};
      }
    }
    &:active:not(:disabled) {
      background-color: ${({ theme }) => theme.c.button.light.primary.background.pressed};
      color: ${({ theme }) => theme.c.button.light.primary.text.pressed};
      border-color: ${({ theme }) => theme.c.button.light.primary.border.pressed};

      & > .icon {
        color: ${({ theme }) => theme.c.button.light.primary.icon.pressed};
      }
    }
    &:disabled {
      background-color: ${({ theme }) => theme.c.button.light.primary.background.disabled};
      color: ${({ theme }) => theme.c.button.light.primary.text.disabled};
      border-color: ${({ theme }) => theme.c.button.light.primary.border.disabled};
      cursor: not-allowed;

      & > .icon {
        color: ${({ theme }) => theme.c.button.light.primary.icon.disabled};
      }
    }
    &:focus {
      ${buttonFocus}
      box-shadow: 0 0 0 2px transparent !important;
    }
  `,
  'secondary-light': css`
    background-color: ${({ theme }) => theme.c.button.light.secondary.background.default};
    color: ${({ theme }) => theme.c.button.light.secondary.text.default};
    border: solid ${({ theme }) => theme.c.button.light.secondary.border.default} ${borderSize};

    & > .icon {
      color: ${({ theme }) => theme.c.button.light.secondary.icon.default};
    }

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.c.button.light.secondary.background.hover};
      color: ${({ theme }) => theme.c.button.light.secondary.text.hover};
      border-color: ${({ theme }) => theme.c.button.light.secondary.border.hover};

      & > .icon {
        color: ${({ theme }) => theme.c.button.light.secondary.icon.hover};
      }
    }
    &:active:not(:disabled) {
      background-color: ${({ theme }) => theme.c.button.light.secondary.background.pressed};
      color: ${({ theme }) => theme.c.button.light.secondary.text.pressed};
      border-color: ${({ theme }) => theme.c.button.light.secondary.border.pressed};

      & > .icon {
        color: ${({ theme }) => theme.c.button.light.secondary.icon.pressed};
      }
    }
    &:disabled {
      background-color: ${({ theme }) => theme.c.button.light.secondary.background.disabled};
      color: ${({ theme }) => theme.c.button.light.secondary.text.disabled};
      border-color: ${({ theme }) => theme.c.button.light.secondary.border.disabled};
      cursor: not-allowed;

      & > .icon {
        color: ${({ theme }) => theme.c.button.light.secondary.icon.disabled};
      }
    }
    &:focus {
      ${buttonFocus}
    }
  `,
  'primary-dark': css`
    background-color: ${({ theme }) => theme.c.button.dark.primary.background.default};
    color: ${({ theme }) => theme.c.button.dark.primary.text.default};
    border: solid ${({ theme }) => theme.c.button.dark.primary.border.default} ${borderSize};

    & > .icon {
      color: ${({ theme }) => theme.c.button.dark.primary.icon.default};
    }

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.c.button.dark.primary.background.hover};
      color: ${({ theme }) => theme.c.button.dark.primary.text.hover};
      border-color: ${({ theme }) => theme.c.button.dark.primary.border.hover};

      & > .icon {
        color: ${({ theme }) => theme.c.button.dark.primary.icon.hover};
      }
    }
    &:active:not(:disabled) {
      background-color: ${({ theme }) => theme.c.button.dark.primary.background.pressed};
      color: ${({ theme }) => theme.c.button.dark.primary.text.pressed};
      border-color: ${({ theme }) => theme.c.button.dark.primary.border.pressed};

      & > .icon {
        color: ${({ theme }) => theme.c.button.dark.primary.icon.pressed};
      }
    }
    &:disabled {
      background-color: ${({ theme }) => theme.c.button.dark.primary.background.disabled};
      color: ${({ theme }) => theme.c.button.dark.primary.text.disabled};
      border-color: ${({ theme }) => theme.c.button.dark.primary.border.disabled};
      cursor: not-allowed;

      & > .icon {
        color: ${({ theme }) => theme.c.button.dark.primary.icon.disabled};
      }
    }
    &:focus {
      ${buttonFocus}
      box-shadow: 0 0 0 2px transparent !important;
    }
  `,
  'secondary-dark': css`
    background-color: ${({ theme }) => theme.c.button.dark.secondary.background.default};
    color: ${({ theme }) => theme.c.button.dark.secondary.text.default};
    border: solid ${({ theme }) => theme.c.button.dark.secondary.border.default} ${borderSize};

    & > .icon {
      color: ${({ theme }) => theme.c.button.dark.secondary.icon.default};
    }

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.c.button.dark.secondary.background.hover};
      color: ${({ theme }) => theme.c.button.dark.secondary.text.hover};
      border-color: ${({ theme }) => theme.c.button.dark.secondary.border.hover};

      & > .icon {
        color: ${({ theme }) => theme.c.button.dark.secondary.icon.hover};
      }
    }
    &:active:not(:disabled) {
      background-color: ${({ theme }) => theme.c.button.dark.secondary.background.pressed};
      color: ${({ theme }) => theme.c.button.dark.secondary.text.pressed};
      border-color: ${({ theme }) => theme.c.button.dark.secondary.border.pressed};

      & > .icon {
        color: ${({ theme }) => theme.c.button.dark.secondary.icon.pressed};
      }
    }
    &:disabled {
      background-color: ${({ theme }) => theme.c.button.dark.secondary.background.disabled};
      color: ${({ theme }) => theme.c.button.dark.secondary.text.disabled};
      border-color: ${({ theme }) => theme.c.button.dark.secondary.border.disabled};
      cursor: not-allowed;

      & > .icon {
        color: ${({ theme }) => theme.c.button.dark.secondary.icon.disabled};
      }
    }
    &:focus {
      ${buttonFocus}
    }
  `,
  'tertiary-light': css`
    ${tertiaryButtonStyles.general}
    color: ${({ theme }) => theme.c.button.light.tertiary.text.default};

    & > .icon {
      color: ${({ theme }) => theme.c.button.light.tertiary.icon.default};
    }

    &:hover:not(:disabled) {
      color: ${({ theme }) => theme.c.button.light.tertiary.text.hover};

      & > .icon {
        color: ${({ theme }) => theme.c.button.light.tertiary.icon.hover};
      }

      ${tertiaryButtonStyles.hoverIconMovement}
    }
    &:active:not(:disabled) {
      color: ${({ theme }) => theme.c.button.light.tertiary.text.pressed};

      & > .icon {
        color: ${({ theme }) => theme.c.button.light.tertiary.icon.pressed};
      }

      ${tertiaryButtonStyles.pressedIconMovement}
    }
    &:disabled {
      color: ${({ theme }) => theme.c.button.light.tertiary.text.disabled};
      cursor: not-allowed;

      & > .icon {
        color: ${({ theme }) => theme.c.button.light.tertiary.icon.disabled};
      }
    }
    &:focus {
      ${buttonFocus}
    }
  `,
  'tertiary-dark': css`
    ${tertiaryButtonStyles.general}
    color: ${({ theme }) => theme.c.button.dark.tertiary.text.default};

    & > .icon {
      color: ${({ theme }) => theme.c.button.dark.tertiary.icon.default};
    }

    &:hover:not(:disabled) {
      color: ${({ theme }) => theme.c.button.dark.tertiary.text.hover};

      & > .icon {
        color: ${({ theme }) => theme.c.button.dark.tertiary.icon.hover};
      }

      ${tertiaryButtonStyles.hoverIconMovement}
    }
    &:active:not(:disabled) {
      color: ${({ theme }) => theme.c.button.dark.tertiary.text.pressed};

      & > .icon {
        color: ${({ theme }) => theme.c.button.dark.tertiary.icon.pressed};
      }

      ${tertiaryButtonStyles.pressedIconMovement}
    }
    &:disabled {
      color: ${({ theme }) => theme.c.button.dark.tertiary.text.disabled};
      cursor: not-allowed;

      & > .icon {
        color: ${({ theme }) => theme.c.button.dark.tertiary.icon.disabled};
      }
    }
    &:focus {
      padding: 0 4px;

      ${buttonFocus}
    }
  `,
};

// Styled Button
const StyledButton = styled.button`
box-sizing: border-box;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  gap: 8px;

  text-decoration: none;

  border-radius: 100px;
  outline-offset: 2px;
  box-shadow: none !important;
  cursor: pointer;

  transition-property: color, background-color, transform, border;
  transition-duration: ${buttonTransition};

  & > .icon {
    width: 24px;
    height: 24px;

    flex-shrink: 0;
  }

  ${({ size }) => buttonDynamicSizes[size || 'medium']};
  ${({ buttonTheming }) => buttonThemes[buttonTheming || 'primary-light']};

  ${({ fullWidth }) => fullWidth && 'width: 100%'};
`;

const StyledLink = StyledButton.withComponent('a');

/**
 * ButtonUI Component
 *
 * A customizable button component that supports various sizes, themes, and variants.
 * It can render as a button or an anchor link and allows the inclusion of left and right icons.
 * No business logic except UI should be added here
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {'small' | 'medium' | 'large'} [props.size='medium'] - The size of the button;
 * applied dynamically depending on screen width
 * @param {'primary' | 'secondary | 'tertiary'} [props.variant='primary'] - The variant of the button.
 * @param {'light' | 'dark'} [props.theme='light'] - The theme of the button.
 * @param {boolean} [props.fullWidth=false] - If true, the button will span the full width of its container.
 * @param {boolean} [props.disabled=false] - If true, the button will be disabled.
 * @param {Function} [props.onClick] - The function to call when the button is clicked.
 * @param {string} [props.href] - If provided, renders the button as a link.
 * @param {string} [props.buttonText] - The text displayed inside the button.
 * @param {Function} [props.renderLeftIcon] - A function that renders a custom icon on the left side of the button.
 * Receives an object with `className` as its property for styling.
 * @param {Function} [props.renderRightIcon] - A function that renders a custom icon on the right side of the button.
 * Receives an object with `className` as its property for styling.
 * @param {React.ReactNode} [props.children] - Additional content to display inside the button.
 * @param {Object} [props.rest] - Additional props to spread onto the button or link element.
 *
 * @example
 * // Rendering a primary button with text
 * <ButtonUI
 *   size="large"
 *   variant="primary"
 *   theme="dark"
 *   onClick={() => alert('Button clicked')}
 *   buttonText="Click Me"
 * />
 *
 * @example
 * // Rendering a button as a link with right icon
 * <ButtonUI
 *   variant="tertiary"
 *   href="https://example.com"
 *   renderRightIcon={({ className }) => <svg className={`${className} fa fa-arrow-right`}></svg>}
 *   buttonText="Go to Example"
 * />
 *
 * @returns {JSX.Element} The rendered ButtonUI component.
 */

const ButtonUI = ({
  size,
  variant,
  theme,
  fullWidth,
  disabled,
  onClick,
  href,
  buttonText,
  renderLeftIcon,
  renderRightIcon,
  children,
  ...rest
}) => {
  const buttonTheming = `${variant}-${theme}`;

  const hasOnlyIcon = Boolean(
    !buttonText && (renderLeftIcon || renderRightIcon)
  );

  const body = (
    <React.Fragment>
      {renderLeftIcon && (
        renderLeftIcon({ className: 'icon icon--left' })
      )}
      {buttonText}
      {children}
      {renderRightIcon && (
        renderRightIcon({ className: 'icon icon--right' })
      )}
    </React.Fragment>
  );

  if (href && !disabled) {
    return (
      <StyledLink
        href={href}
        size={size}
        buttonTheming={buttonTheming}
        fullWidth={fullWidth}
        hasOnlyIcon={hasOnlyIcon}
        disabled={disabled}
        onClick={onClick}
        {...rest}
      >
        {body}
      </StyledLink>
    );
  }

  return (
    <StyledButton
      size={size}
      buttonTheming={buttonTheming}
      fullWidth={fullWidth}
      hasOnlyIcon={hasOnlyIcon}
      disabled={disabled}
      onClick={onClick}
      {...rest}
    >
      {body}
    </StyledButton>
  );
};

// PropTypes Validation
ButtonUI.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
  theme: PropTypes.oneOf(['light', 'dark']),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  href: PropTypes.string,
  buttonText: PropTypes.string,
  renderLeftIcon: PropTypes.func,
  renderRightIcon: PropTypes.func,
  children: PropTypes.node,
};

ButtonUI.defaultProps = {
  size: 'medium',
  variant: 'primary',
  theme: 'light',
  fullWidth: false,
  disabled: false,
  onClick: undefined,
  href: '',
  buttonText: '',
  renderLeftIcon: undefined,
  renderRightIcon: undefined,
  children: undefined,
};

export default ButtonUI;
